import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import ShiftReportDepartmentForm from "@/modules/shiftReportDepartments/components/ShiftReportDepartmentForm";
import { useShiftReportDepartmentsDictionary } from "@/modules/shiftReportDepartments/hooks/useShiftReportDepartmentsDictionary";
import { ApiError } from "@/services/api";
import {
  ReadShiftReportDepartment,
  WriteShiftReportDepartment,
} from "@/types/shiftReportDepartment";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function ShiftReportDepartmentCreator() {
  const id = "new";
  const { t } = useTranslation();
  const config = useShiftReportDepartmentsDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { invalidateListQuery } = useQueryCacheUpdater(
    Module.ShiftReportDepartments,
  );
  const labels = config.getLabels(t);
  const { can_create } = useModulePermissions();
  const mutationFn: MutationFunction<
    ReadShiftReportDepartment,
    WriteShiftReportDepartment
  > = async (values) => {
    const response = await config.service.post(values);
    return response;
  };

  const mutation = useMutation<
    ReadShiftReportDepartment,
    ApiError,
    WriteShiftReportDepartment
  >({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WriteShiftReportDepartment>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<WriteShiftReportDepartment>,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <ShiftReportDepartmentForm />
    </DictionaryRecordContextProvider>
  );
}
